import { institutionalApi } from "./index";
import {
  RouterSupportedDOCountry,
  RouterSupportedFiat,
  RouterTransferMethods,
  SyntheticRouterSupportedFiat,
} from "interfaces/enums";

import {
  ApiKey,
  IIndicativeQuoteRequest,
  IIndicativeQuoteResponse,
  IRouterQuoteRequest,
  IRouterQuoteResponse,
  IRouterSubAccount,
  Webhook,
  WebhookCreate,
} from "interfaces/router";
import { transformErrorResponse, transformToValidFiat } from "utils";

export const ccyConversionOrdersrouter = institutionalApi.injectEndpoints({
  endpoints: (builder) => ({
    getRouterQuote: builder.mutation<IRouterQuoteResponse, IRouterQuoteRequest>(
      {
        query: (body: IRouterQuoteRequest) => ({
          url: `/router/quote`,
          body: {
            ...body,
            ccy_source: transformToValidFiat(body.ccy_source as any),
          },
          method: "POST",
        }),
        transformErrorResponse,
        transformResponse: (response: IRouterQuoteResponse) => {
          return {
            ...response,
            // Simulates the upcoming BE `ttl` field (ms the quote lives for)
            ttl:
              response.ttl ??
              (response.time_expires - response.time_created) * 1000,
            ccy_source:
              response.country_dest === RouterSupportedDOCountry.US &&
              response.ccy_source === RouterSupportedFiat.USD
                ? SyntheticRouterSupportedFiat.MexDolar
                : (response.ccy_source as any),
          };
        },
      },
    ),
    getRouterQuoteQuery: builder.query<
      IRouterQuoteResponse,
      // instance_key is a client-only cache discriminator: it lets multiple
      // order cards with identical params keep independent quotes (separate
      // API calls / quote ids). It is stripped before the request is sent.
      IRouterQuoteRequest & { instance_key?: string }
    >({
      query: ({ instance_key, ...body }) => {
        const transformedBody = { ...body };
        if (!transformedBody.quote_target) {
          delete transformedBody.quote_target_inverted;
        }
        transformedBody.ccy_source = transformToValidFiat(
          body.ccy_source as any,
        );
        return {
          url: `/router/quote`,
          body: transformedBody,
          method: "POST",
        };
      },
      transformErrorResponse,
      transformResponse: (response: IRouterQuoteResponse) => {
        return {
          ...response,
          // Simulates the upcoming BE `ttl` field (ms the quote lives for)
          ttl: (response.time_expires - response.time_created) * 1000,
          ccy_source:
            response.country_dest === RouterSupportedDOCountry.US &&
            response.ccy_source === RouterSupportedFiat.USD
              ? SyntheticRouterSupportedFiat.MexDolar
              : (response.ccy_source as any),
        };
      },
    }),
    // Display-only rate for a corridor at a fixed 100k USDC notional. The
    // requesting user's spread is applied server-side, so there is nothing to
    // pass beyond the corridor itself. Not tradable — always create a real
    // quote via /router/quote before placing an order.
    getIndicativeQuote: builder.query<
      IIndicativeQuoteResponse,
      IIndicativeQuoteRequest
    >({
      query: (params) => ({
        url: `/router/indicative_quote`,
        method: "GET",
        params: {
          // callers may hold the synthetic MexDolar value for a USD source
          ccy_source: transformToValidFiat(params.ccy_source as any),
          ccy_dest: params.ccy_dest,
          // left off entirely when unset so the backend applies its own SPOT default
          ...(params.settlement_type
            ? { settlement_type: params.settlement_type }
            : {}),
        },
      }),
      // the server caches each corridor for ~20s; holding the cache entry that
      // long keeps pair-toggling from re-hitting the LPs needlessly
      keepUnusedDataFor: 20,
      transformErrorResponse,
    }),
    getRouterSubAccounts: builder.query<Array<IRouterSubAccount>, void>({
      query: (params) => ({
        url: `/router/broker/sub_accounts`,
        method: "GET",
      }),
      providesTags: [{ type: "RouterSubAccounts" }],
      transformErrorResponse,
    }),
    getSuperBrokerSubAccounts: builder.query<
      Array<{
        id: String;
        alias: string;
        sub_accounts: Array<IRouterSubAccount>;
      }>,
      void
    >({
      query: (params) => ({
        url: `/router/super_broker/sub_accounts`,
        method: "GET",
      }),
      providesTags: [{ type: "RouterSubAccounts" }],
      transformErrorResponse,
    }),

    simulateRouterDeposit: builder.mutation<
      string,
      { order_id: string; ccy: RouterSupportedFiat; qty: string }
    >({
      query: (body) => ({
        url: "/router/simulate/user_transfer",
        method: "POST",
        body: { ...body, ccy: transformToValidFiat(body.ccy as any) },
      }),
      transformErrorResponse,
    }),
    simulateRouterTransferToLp: builder.mutation<string, { order_id: string }>({
      query: (body) => ({
        url: "/router/simulate/transfer_to_lp",
        method: "POST",
        body,
      }),
      transformErrorResponse,
    }),
    simulateUserTransfer: builder.mutation<string, { order_id: string }>({
      query: (body) => ({
        url: "/router/simulate/user_transfer",
        method: "POST",
        body,
      }),
      transformErrorResponse,
    }),
    getReceiptDocument: builder.query<
      string,
      {
        lang?: string;
        orderId: string;
        dest_transfer_only?: boolean;
        consolidating_only?: boolean;
      }
    >({
      query: (params) => ({
        url: `/router/receipt_pdf/${params.orderId}`,
        params: {
          language: params.lang ?? "es",
          dest_transfer_only: params.dest_transfer_only?.toString(),
          consolidating_only: params.consolidating_only?.toString(),
        },
      }),
      transformErrorResponse,
    }),

    formOptions: builder.query<
      Record<string, Array<RouterTransferMethods>>,
      { ccy_dest: string }
    >({
      query: (params) => ({
        url: "/router/form_options",
        method: "GET",
        params,
      }),
      transformErrorResponse,
    }),
    countryOptions: builder.query<Array<{ name: string; code: string }>, void>({
      query: () => ({
        url: "/router/countries",
        method: "GET",
      }),
      transformErrorResponse,
    }),
    downloadCFDI: builder.query<
      string,
      { orderId: string; document_type: "pdf" | "xml" }
    >({
      query: (body) => ({
        url: `/router/cfdi/${body.orderId}`,
        method: "GET",
        params: { document_type: body.document_type },
      }),
      transformErrorResponse,
    }),
    createApiKey: builder.mutation<
      {
        api_key: string;
        api_secret: string;
      },
      void
    >({
      query: () => ({
        url: `/auth/api_keys`,
        method: "POST",
      }),
      invalidatesTags: ["ApiKeys"],
      transformErrorResponse,
    }),
    getApiKeys: builder.query<Array<ApiKey>, void>({
      query: () => ({
        url: `/auth/api_keys`,
        method: "GET",
      }),
      providesTags: ["ApiKeys"],
      transformErrorResponse,
    }),
    deleteApiKeys: builder.mutation<void, { id: string }>({
      query: (body) => ({
        url: `/auth/api_keys/${body.id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ApiKeys"],
      transformErrorResponse,
    }),
    createWebhook: builder.mutation<void, WebhookCreate>({
      query: (body) => ({
        url: `/webhooks`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Webhooks"],
      transformErrorResponse,
    }),
    deleteWebhook: builder.mutation<void, { id: string }>({
      query: (body) => ({
        url: `/webhooks/${body.id}`,
        method: "Delete",
        body,
      }),
      invalidatesTags: ["Webhooks"],
      transformErrorResponse,
    }),
    getWebhook: builder.query<Array<Webhook>, void>({
      query: () => ({
        url: `/webhooks`,
        method: "GET",
      }),
      providesTags: ["Webhooks"],
      transformErrorResponse,
    }),
    getPurposeOfPayments: builder.query<
      Array<{ label: string; enum_name: string }>,
      void
    >({
      query: () => ({
        url: `/router/purpose_of_payment`,
        method: "GET",
      }),
      providesTags: ["PurposeOfPayments"],
      transformErrorResponse,
    }),
  }),
});

export const {
  useGetIndicativeQuoteQuery,
  useLazyGetIndicativeQuoteQuery,
  useLazyGetRouterQuoteQueryQuery,
  useGetPurposeOfPaymentsQuery,
  useGetSuperBrokerSubAccountsQuery,
  useGetRouterQuoteQueryQuery,
  useGetWebhookQuery,
  useDeleteApiKeysMutation,
  useDeleteWebhookMutation,
  useCreateWebhookMutation,
  useGetApiKeysQuery,
  useCreateApiKeyMutation,
  useLazyDownloadCFDIQuery,
  useCountryOptionsQuery,
  useFormOptionsQuery,
  useLazyGetReceiptDocumentQuery,
  useGetReceiptDocumentQuery,
  useGetRouterQuoteMutation,
  useGetRouterSubAccountsQuery,
  useSimulateRouterDepositMutation,
  useSimulateUserTransferMutation,
  useSimulateRouterTransferToLpMutation,
} = ccyConversionOrdersrouter;
